import { useCallback, useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  Header,
  Row,
} from '@tanstack/react-table';
import ChevronIcon from '@icons/inputs/chevron_icon.svg?component';
import { Obj } from '@/types/common';
import { MyColumnMeta } from '@/types/table';
import { TableProps } from './types';
import {
  BASIC_CELL_HEIGHT,
  HEIGTH_UNDER_TABLE,
  ITEMS_ON_PAGE,
  MAX_PAGINATION_BUTTONS,
} from './constants';
import { useWindowSize } from '@/hooks/useWindowSize';

const useFlexLayout = true;

function Table<T extends Obj, V = unknown>({
  data,
  columns,
  tableWidth = '100%',
}: TableProps<T, V>) {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: ITEMS_ON_PAGE,
  });

  const { getHeaderGroups, getRowModel, getRowCount, setPageIndex } = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
  });

  const [tableBodyHeight, setTableBodyHeight] = useState(220);

  const windowSize = useWindowSize();

  useEffect(() => {
    const top = document.getElementById('table-header')?.getBoundingClientRect().bottom || 0;
    const bodyHeight = (windowSize?.wHeight || window.innerHeight) - top - HEIGTH_UNDER_TABLE;
    const rowsHeight = getRowCount() * BASIC_CELL_HEIGHT;
    setTableBodyHeight(Math.min(rowsHeight, bodyHeight));
  }, [windowSize, windowSize?.wHeight, data]);

  const getCellClass = useCallback((className: string, meta?: MyColumnMeta) => {
    if (useFlexLayout) {
      return `${className} ${meta?.grow ? 'flex-1 min-w-[100px]' : ''} ${meta?.className}`;
    }
    return `${className} ${
      meta?.grow ? 'w-full min-w-[100px]' : ''
    } whitespace-nowrap px-4 py-[12px] ${meta?.className}`;
  }, []);

  const renderHeaderCell = useCallback(
    ({ column, id, isPlaceholder, getContext }: Header<T, unknown>) => {
      const { meta } = column.columnDef;
      return (
        <div
          key={id}
          className={getCellClass(
            'text-left font-semibold uppercase small-text py-[16px] flex items-center',
            meta
          )}
          style={meta?.width ? { width: meta.width, minWidth: meta.width } : {}}
          onClick={column.getToggleSortingHandler()}
          title={
            column.getCanSort()
              ? column.getNextSortingOrder() === 'asc'
                ? 'Sort ascending'
                : column.getNextSortingOrder() === 'desc'
                  ? 'Sort descending'
                  : 'Clear sort'
              : undefined
          }
        >
          {isPlaceholder ? null : flexRender(column.columnDef.header, getContext())}
          {column.getIsSorted() === 'asc' && <ChevronIcon width={16} height={16} />}
          {column.getIsSorted() === 'desc' && (
            <ChevronIcon width={16} height={16} className="rotate-180" />
          )}
        </div>
      );
    },
    [data]
  );

  const renderBodyRow = useCallback(
    ({ id, getVisibleCells }: Row<T>, index: number) => {
      return (
        <div
          key={id}
          className={`${
            useFlexLayout ? 'flex w-full gap-x-[16px] px-[48px] sm-page-padding' : 'table-row'
          } ${!(index % 2) && 'bg-white'}`}
        >
          {getVisibleCells().map((cell) => {
            const { meta } = cell.column.columnDef;
            return (
              <div
                key={cell.id}
                className={getCellClass('flex-centred py-[12px] ', meta)}
                style={meta?.width ? { width: meta.width, minWidth: meta.width } : {}}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            );
          })}
        </div>
      );
    },
    [useFlexLayout, data]
  );

  const renderPagination = useCallback(() => {
    const arrayLength = Math.ceil(data.length / pagination.pageSize);
    let startNum = Math.max(0, pagination.pageIndex - Math.floor(MAX_PAGINATION_BUTTONS / 2));
    const endNum = Math.min(arrayLength, Math.max(0, startNum + MAX_PAGINATION_BUTTONS));
    startNum = Math.max(endNum - MAX_PAGINATION_BUTTONS, 0);
    return Array.from({ length: arrayLength })
      .slice(startNum, endNum)
      .map((_, i) => (
        <button
          key={startNum + i}
          onClick={() => setPageIndex(startNum + i)}
          className={`w-[32px] h-[32px] rounded-full text-sm ${
            pagination.pageIndex === startNum + i
              ? 'bg-primary text-white'
              : 'bg-secondary text-primary'
          }`}
        >
          {startNum + i + 1}
        </button>
      ));
  }, [pagination.pageIndex, data]);

  return (
    // <div className="w-full overflow-x-auto">
    // <div className="w-full overflow-x-auto">
    <div
      className={
        useFlexLayout ? 'w-full overflow-x-auto flex flex-col' : 'table w-full border-collapse'
      }
    >
      {/* <div className={false ? 'flex flex-col w-full' : 'table w-full border-collapse'}> */}
      {/* Table Head */}
      <div
        id="table-header"
        className={useFlexLayout ? 'flex' : 'table-header-group'}
        style={{ minWidth: tableWidth }}
      >
        {getHeaderGroups().map((headerGroup) => (
          <div
            key={headerGroup.id}
            className={
              useFlexLayout ? 'flex w-full gap-x-[16px] px-[48px] sm-page-padding' : 'table-row'
            }
          >
            {headerGroup.headers.map(renderHeaderCell)}
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div
        id="table-body"
        className={`${
          useFlexLayout
            ? 'flex flex-col overflow-y-auto overflow-x-hidden w-fit'
            : 'table-row-group'
        }`}
        style={{ height: tableBodyHeight, minWidth: tableWidth }}
      >
        {getRowModel().rows.map(renderBodyRow)}
      </div>
      {/* Pagination buttons */}
      {data.length > ITEMS_ON_PAGE && (
        <div id="table-pagination" className="flex justify-center mt-[32px] gap-[8px]">
          {renderPagination()}
        </div>
      )}
    </div>
    // </div>
  );
}

export default Table;
